<template>
  <section class="mt-8">
    <h3 v-if="title" class="mb-4 text-lg text-neutral-550">{{ title }}</h3>
    <div v-if="!usageItems.length" class="core-split-panes border p-2 text-neutral-550">
      {{ $t('project.general.sections.usage_definition.usage.hint.init') }}
    </div>

    <div v-else class="border-t border-neutral-300">
      <template v-for="(usage, index) in usageItems" :key="`${usage.type}-${usage.typeCounter}-${index}`">
        <AccordionItem
          :title="getAccordionTitle(usage, index)"
          :description="getAccordionSelectionsSubtitle(usage)"
          :triggerTooltip="showAdjustmentsTooltip ? getAccordionAdjustmentsTooltip(index) : undefined"
          :open="activeAccordionKey === getAccordionKey(index)"
          :controlledOpen="true"
          @toggle="onAccordionToggle(index, $event)">
          <h3 class="mb-4 text-lg">{{ getRequirementsLevelTitle() }}</h3>
          <div class="boxed-button-grid requirements-level-grid">
            <label
              v-for="level in requirementsLevels"
              :key="`${level}-${index}`"
              class="base-button"
              :class="{ active: getRequirementsLevel(usage) === level }">
              {{ getRequirementsLevelLabel(level) }}
              <input
                type="radio"
                :value="level"
                :checked="getRequirementsLevel(usage) === level"
                :id="`${level}-${index}`"
                :name="`requirementsLevel-${index}`"
                @change="setRequirementsLevel(index, level)" />
            </label>
          </div>

          <h3 class="mt-12 mb-4 text-lg">{{ wt('requirementsAndTasks', 'task', 'title') }}</h3>
          <div class="boxed-button-grid">
            <label
              v-for="plan in buildingPlanOptions"
              :key="`${plan}-${index}`"
              v-tooltip="getBuildingPlanUnavailableTooltip(plan)"
              class="base-button"
              :class="{
                active: usage.buildingPlan === plan,
                disabled: isBuildingPlanUnavailable(plan),
                'base-button--tooltip-enabled': isBuildingPlanUnavailable(plan),
              }"
              :aria-disabled="isBuildingPlanUnavailable(plan) ? 'true' : undefined">
              {{ wt('requirementsAndTasks', 'task', 'planType', 'option', plan) }}
              <input
                type="radio"
                :value="plan"
                :checked="usage.buildingPlan === plan"
                :disabled="isBuildingPlanUnavailable(plan)"
                :id="`${plan}-${index}`"
                :name="`buildingPlan-${index}`"
                @change="setBuildingPlan(index, plan)" />
            </label>
          </div>

          <Transition name="task-sliders">
            <div v-if="showSliders(usage.buildingPlan)" class="task-sliders-panel">
              <div class="task-sliders-panel-inner">
                <h3 class="mt-12 mb-6 text-lg">
                  {{ wt('requirementsAndTasks', 'task', 'depthOfIntervention', 'title') }}
                </h3>
                <div class="task-slider-list">
                  <div class="task-slider-scale-title-spacer" aria-hidden="true"></div>
                  <div class="task-slider-scale-cell">
                    <div class="task-slider-controls">
                      <div class="task-slider-scale">
                        <div class="flex justify-between font-bold text-neutral-550">
                          <span class="font-medium">0%</span>
                          <span class="font-medium">100%</span>
                        </div>
                      </div>
                      <div class="task-slider-scale-spacer" aria-hidden="true"></div>
                    </div>
                  </div>

                  <template v-for="item in interventionFields" :key="`${item}-${index}`">
                    <div
                      v-tooltip="getInterventionTooltip(item)"
                      class="task-slider-title-cell text-base text-neutral-550 lg:pr-6 lg:text-right"
                      :class="{ 'cursor-help': !!getInterventionTooltip(item) }">
                      {{ wt('requirementsAndTasks', 'task', 'depthOfIntervention', interventionOptionKey, item) }}
                    </div>
                    <div class="task-slider-control-cell">
                      <div class="task-slider-controls">
                        <div class="task-slider-range slider-wrapper relative min-w-0 flex-1">
                          <div class="task-slider-markers" aria-hidden="true">
                            <span
                              v-for="sliderStep in interventionSliderSteps"
                              :key="`${item}-${index}-${sliderStep}`"
                              class="task-slider-marker"
                              :class="{ active: sliderStep <= getSliderDisplayValue(index, item) }"
                              :style="{ left: `${sliderStep}%` }"></span>
                          </div>
                          <!-- @vue-ignore -->
                          <RangeInput
                            :modelValue="getSliderDisplayValue(index, item)"
                            colorVariant="primary"
                            :tabindex="-1"
                            :min="INTERVENTION_SLIDER_MIN"
                            :max="INTERVENTION_SLIDER_MAX"
                            :step="INTERVENTION_SLIDER_STEP"
                            @input="onSliderInput(index, item, $event)"
                            @change="onSliderChange(index, item, $event)" />
                          <!-- @vue-ignore -->
                          <span
                            :style="{ left: calculatePosition(getSliderDisplayValue(index, item)) }"
                            class="slider-value absolute -bottom-4 -translate-x-1/2 whitespace-nowrap text-neutral-550 lg:-bottom-1">
                            {{ formatSliderPercentageDisplay(getSliderDisplayValue(index, item)) }}%
                          </span>
                        </div>

                        <div class="task-slider-input-group">
                          <button
                            type="button"
                            class="adjust-value-button"
                            tabindex="-1"
                            :aria-label="`${String(item)} -10%`"
                            @click="adaptSliderValue(index, item, -INTERVENTION_SLIDER_STEP)">
                            <IconMinus class="h-4 w-4" stroke="2" />
                          </button>
                          <div class="flex items-center">
                            <input
                              :value="formatSliderPercentageDisplay(getSliderDisplayValue(index, item))"
                              type="number"
                              :min="INTERVENTION_SLIDER_MIN"
                              :max="INTERVENTION_SLIDER_MAX"
                              :step="INTERVENTION_SLIDER_STEP"
                              class="core-form-input mb-0 w-14 text-center text-primary-dark md:w-16"
                              @input="onSliderNumberInput(index, item, $event)"
                              @change="onSliderNumberCommit(index, item, $event)"
                              @blur="onSliderNumberCommit(index, item, $event)" />
                            <span class="ml-1 text-sm text-neutral-550">%</span>
                          </div>
                          <button
                            type="button"
                            class="adjust-value-button"
                            tabindex="-1"
                            :aria-label="`${String(item)} +10%`"
                            @click="adaptSliderValue(index, item, INTERVENTION_SLIDER_STEP)">
                            <IconPlus class="h-4 w-4" stroke="2" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </Transition>

          <div class="task-factor-row">
            <span class="font-medium text-neutral-700">{{ getTotalBuildingPlanTaskFactorLabel() }}</span>
            <span class="text-xl font-bold text-primary-default">
              {{ formatAdjustmentFactor(getTotalBuildingPlanTaskFactor(index)) }}
            </span>
          </div>

          <div v-if="showRepetitionFactor" class="mt-12">
            <h3 class="mb-6 text-lg">
              {{ wt('requirementsAndTasks', 'task', 'repetitionFactor', 'title') || 'Repetition factor SIA 103' }}
            </h3>
            <div class="task-slider-list">
              <div
                class="task-slider-title-cell repetition-slider-label text-base text-neutral-550 lg:pr-6 lg:text-right"
                :title="getRepetitionFactorText('lowLabel', 'Many repetitions')">
                {{ getRepetitionFactorText('lowLabel', 'Many repetitions') }}
              </div>
              <div class="task-slider-control-cell">
                <div class="task-slider-controls">
                  <div class="task-slider-range slider-wrapper relative min-w-0 flex-1">
                    <div class="task-slider-markers" aria-hidden="true">
                      <span
                        v-for="sliderStep in repetitionSliderSteps"
                        :key="`${index}-repetition-${sliderStep}`"
                        class="task-slider-marker"
                        :class="{ active: sliderStep <= getRepetitionFactor(index) }"
                        :style="{ left: `${getRepetitionSliderPercent(sliderStep)}%` }"></span>
                    </div>
                    <!-- @vue-ignore -->
                    <RangeInput
                      :modelValue="getRepetitionFactor(index)"
                      colorVariant="primary"
                      :tabindex="-1"
                      :min="REPETITION_FACTOR_MIN"
                      :max="REPETITION_FACTOR_MAX"
                      :step="REPETITION_FACTOR_STEP"
                      @input="onRepetitionFactorInput(index, $event)"
                      @change="onRepetitionFactorChange(index, $event)" />
                    <!-- @vue-ignore -->
                    <span
                      :style="{ left: calculateRepetitionPosition(getRepetitionFactor(index)) }"
                      class="slider-value absolute -bottom-4 -translate-x-1/2 whitespace-nowrap text-neutral-550 lg:-bottom-1">
                      {{ formatAdjustmentFactor(getRepetitionFactor(index)) }}
                    </span>
                  </div>

                  <div
                    class="repetition-slider-label repetition-slider-end-label text-right text-base text-neutral-550"
                    :title="getRepetitionFactorText('highLabel', 'Few repetitions')">
                    {{ getRepetitionFactorText('highLabel', 'Few repetitions') }}
                  </div>
                </div>
              </div>
            </div>
            <div class="task-factor-row mt-6">
              <span class="font-medium text-neutral-700">
                {{ wt('requirementsAndTasks', 'task', 'repetitionFactor', 'valueLabel') || 'Repetition factor' }}
              </span>
              <span class="text-xl font-bold text-primary-default">
                {{ formatAdjustmentFactor(getRepetitionFactor(index)) }}
              </span>
            </div>
          </div>

          <div v-if="showTechnologyFactor" class="mt-12">
            <h3 class="mb-6 text-lg">
              {{ getTechnologyFactorText('title', 'Technology degree SIA 108') }}
            </h3>
            <div class="task-slider-list">
              <div
                class="task-slider-title-cell repetition-slider-label text-base text-neutral-550 lg:pr-6 lg:text-right"
                :title="getTechnologyFactorText('lowLabel', 'Low technology degree')">
                {{ getTechnologyFactorText('lowLabel', 'Low technology degree') }}
              </div>
              <div class="task-slider-control-cell">
                <div class="task-slider-controls">
                  <div class="task-slider-range slider-wrapper relative min-w-0 flex-1">
                    <div class="task-slider-markers" aria-hidden="true">
                      <span
                        v-for="sliderStep in technologySliderSteps"
                        :key="`${index}-technology-${sliderStep}`"
                        class="task-slider-marker"
                        :class="{ active: sliderStep <= getTechnologyFactor(index) }"
                        :style="{ left: `${getTechnologySliderPercent(sliderStep)}%` }"></span>
                    </div>
                    <!-- @vue-ignore -->
                    <RangeInput
                      :modelValue="getTechnologyFactor(index)"
                      colorVariant="primary"
                      :tabindex="-1"
                      :min="TECHNOLOGY_FACTOR_MIN"
                      :max="TECHNOLOGY_FACTOR_MAX"
                      :step="TECHNOLOGY_FACTOR_STEP"
                      @input="onTechnologyFactorInput(index, $event)"
                      @change="onTechnologyFactorChange(index, $event)" />
                    <!-- @vue-ignore -->
                    <span
                      :style="{ left: calculateTechnologyPosition(getTechnologyFactor(index)) }"
                      class="slider-value absolute -bottom-4 -translate-x-1/2 whitespace-nowrap text-neutral-550 lg:-bottom-1">
                      {{ formatAdjustmentFactor(getTechnologyFactor(index)) }}
                    </span>
                  </div>

                  <div
                    class="repetition-slider-label repetition-slider-end-label text-right text-base text-neutral-550"
                    :title="getTechnologyFactorText('highLabel', 'High technology degree')">
                    {{ getTechnologyFactorText('highLabel', 'High technology degree') }}
                  </div>
                </div>
              </div>
            </div>
            <div class="task-factor-row mt-6">
              <span class="font-medium text-neutral-700">
                {{ getTechnologyFactorText('valueLabel', 'Technology degree factor') }}
              </span>
              <span class="text-xl font-bold text-primary-default">
                {{ formatAdjustmentFactor(getTechnologyFactor(index)) }}
              </span>
            </div>
          </div>
        </AccordionItem>
      </template>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, type PropType } from 'vue'
import { LANDSCAPE_INTERVENTION_FIELDS, type LandscapeInterventionField } from '@arcexperts/core-types'
import type { SharedUsageItem } from '@/schemas/general/types'
import { BuildingPlan } from '@/schemas/general/enums'
import { IconMinus, IconPlus } from '@tabler/icons-vue'
import RangeInput from '@/components/partials/inputs/RangeInput.vue'
import AccordionItem from '@/components/partials/layout/AccordionItem.vue'
import { useProjectGeneralCalculator } from '@/stores/projectCenter/projectGeneralCalculator'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getUsageDisplayTitle } from '@/helpers/usage-display'
import { useContentStore } from '@/stores/content'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

const contentStore = useContentStore()
const props = withDefaults(
  defineProps<{
    title?: string
    usageItemsKey?: 'usageItems' | 'landscapeUsageItems'
    role?: Role
    activeAccordionKey?: string | null
  }>(),
  {
    title: '',
    usageItemsKey: 'usageItems',
    role: undefined,
    activeAccordionKey: null,
  }
)
const { wt } = useWidgetText(
  'widget-texts-project-general',
  computed(() => props.role)
)
const emit = defineEmits<{
  'update:activeAccordionKey': [value: string | null]
}>()
const modelValue = defineModel({ type: Object as PropType<Record<string, unknown>> })
const calculations = useProjectGeneralCalculator()
const sliderDraftValues = ref<Record<string, number>>({})
const showAdjustmentsTooltip = import.meta.env.DEV
const INTERVENTION_SLIDER_MIN = 0
const INTERVENTION_SLIDER_MAX = 100
const INTERVENTION_SLIDER_STEP = 10
const DEFAULT_INTERVENTION_SLIDER_VALUE = 30
const INTERVENTION_SLIDER_THUMB_RADIUS_PX = 10
const REPETITION_FACTOR_MIN = 0.8
const REPETITION_FACTOR_MAX = 1.4
const REPETITION_FACTOR_STEP = 0.1
const DEFAULT_REPETITION_FACTOR = 1.0
const TECHNOLOGY_FACTOR_MIN = 0.8
const TECHNOLOGY_FACTOR_MAX = 1.4
const TECHNOLOGY_FACTOR_STEP = 0.1
const DEFAULT_TECHNOLOGY_FACTOR = 1.0
const interventionTooltipTheme = { 'max-width': '350px' } as const

type RequirementsLevel = 'LOW' | 'STANDARD' | 'HIGH' | 'VERY_HIGH'

type UsageWithRequirementsLevel = SharedUsageItem & {
  requirementsLevel?: RequirementsLevel | string | null
  buildingTypeName?: string | null
}

type InterventionUsageItem = SharedUsageItem & {
  facadeEnvelope?: number
  buildingTypeId?: string | null
  buildingTypeName?: string | null
  requirementsLevel?: RequirementsLevel | string | null
  repetitionFactor?: number | null
  technologyFactor?: number | null
}

const requirementsLevels: RequirementsLevel[] = ['LOW', 'STANDARD', 'HIGH', 'VERY_HIGH']
const buildingPlanOptions: BuildingPlan[] = [
  BuildingPlan.NEW_BUILDING,
  BuildingPlan.RECONSTRUCTION,
  BuildingPlan.EXTENSION,
]
const DEFAULT_REQUIREMENTS_LEVEL: RequirementsLevel = 'STANDARD'
const fallbackRequirementsLevelLabels: Record<RequirementsLevel, string> = {
  LOW: 'Low',
  STANDARD: 'Standard',
  HIGH: 'High',
  VERY_HIGH: 'Very high',
}

const buildingInterventionFields = [
  'rawConstruction',
  'facadeEnvelope',
  'interiors',
  'electricInstallation',
  'heatingVentilation',
  'sanitary',
] as const
type InterventionField = (typeof buildingInterventionFields)[number] | LandscapeInterventionField

// VAL-152: landscape usages have their own reconstruction items, stored under their own keys
// and labelled from their own Payload texts.
const isLandscapeUsageSet = computed(() => props.usageItemsKey === 'landscapeUsageItems')
const interventionFields = computed<readonly InterventionField[]>(() =>
  isLandscapeUsageSet.value ? LANDSCAPE_INTERVENTION_FIELDS : buildingInterventionFields
)
const interventionOptionKey = computed(() => (isLandscapeUsageSet.value ? 'landscapeOption' : 'option'))

const interventionSliderSteps = Array.from(
  { length: (INTERVENTION_SLIDER_MAX - INTERVENTION_SLIDER_MIN) / INTERVENTION_SLIDER_STEP + 1 },
  (_, index) => INTERVENTION_SLIDER_MIN + index * INTERVENTION_SLIDER_STEP
)
const repetitionSliderSteps = Array.from(
  { length: Math.round((REPETITION_FACTOR_MAX - REPETITION_FACTOR_MIN) / REPETITION_FACTOR_STEP) + 1 },
  (_, index) => Number((REPETITION_FACTOR_MIN + index * REPETITION_FACTOR_STEP).toFixed(1))
)
const technologySliderSteps = Array.from(
  { length: Math.round((TECHNOLOGY_FACTOR_MAX - TECHNOLOGY_FACTOR_MIN) / TECHNOLOGY_FACTOR_STEP) + 1 },
  (_, index) => Number((TECHNOLOGY_FACTOR_MIN + index * TECHNOLOGY_FACTOR_STEP).toFixed(1))
)
const technologyFactorRoles: Role[] = [
  'ELECTRICAL',
  'HEATING_COOLING',
  'VENTILATION_AC',
  'SANITARY',
  'BUILDING_AUTOMATION',
  'SPECIALIST_COORDINATION',
]

const buildingPlanCostFactorsForUsages = computed(() => calculations.getBuildingPlanCostFactorsForUsages)
const buildingPlanServiceFactorsForUsages = computed(() => calculations.getBuildingPlanServiceFactorsForUsages)
const buildingPlanCostFactorsForLandscapeUsages = computed(
  () => calculations.getBuildingPlanCostFactorsForLandscapeUsages
)
const buildingPlanServiceFactorsForLandscapeUsages = computed(
  () => calculations.getBuildingPlanServiceFactorsForLandscapeUsages
)
const buildingPlanCostFactorsForRoleUsages = computed(() =>
  props.role ? calculations.getBuildingPlanCostFactorsForRoleUsages(props.role) : undefined
)
const buildingPlanServiceFactorsForRoleUsages = computed(() =>
  props.role ? calculations.getBuildingPlanServiceFactorsForRoleUsages(props.role) : undefined
)

function ensureRoleUsageItemsContainer(): Record<string, InterventionUsageItem[]> {
  const current = modelValue.value?.roleUsageItems
  if (current && typeof current === 'object' && !Array.isArray(current)) {
    return current as Record<string, InterventionUsageItem[]>
  }
  return {}
}

function getBaseUsageItems(): InterventionUsageItem[] {
  return (modelValue.value?.[props.usageItemsKey] ?? []) as InterventionUsageItem[]
}

function mergeRoleUsageItem(
  baseUsage: InterventionUsageItem,
  roleUsage: InterventionUsageItem | undefined
): InterventionUsageItem {
  return {
    ...baseUsage,
    ...(roleUsage ?? {}),
    type: baseUsage.type,
    typeCounter: baseUsage.typeCounter,
    ratio: baseUsage.ratio,
    buildingTypeId: baseUsage.buildingTypeId,
    buildingTypeName: baseUsage.buildingTypeName,
  }
}

function getRoleUsageItems(): InterventionUsageItem[] {
  const baseUsageItems = getBaseUsageItems()
  if (!props.role || !modelValue.value) return baseUsageItems

  const roleUsageItemsContainer = ensureRoleUsageItemsContainer()
  const currentRoleUsageItems = roleUsageItemsContainer[props.role] ?? []
  return baseUsageItems.map((usage, index) => mergeRoleUsageItem(usage, currentRoleUsageItems[index]))
}

const usageItems = computed<InterventionUsageItem[]>({
  get: () => getRoleUsageItems(),
  set: (value) => {
    if (!modelValue.value) return
    if (props.role) {
      modelValue.value = {
        ...modelValue.value,
        roleUsageItems: {
          ...ensureRoleUsageItemsContainer(),
          [props.role]: value,
        },
      }
      return
    }
    modelValue.value = {
      ...modelValue.value,
      [props.usageItemsKey]: value,
    }
  },
})

function updateUsageItem(index: number, updates: Partial<InterventionUsageItem>): void {
  const currentUsageItems = usageItems.value
  if (!currentUsageItems[index]) return

  usageItems.value = currentUsageItems.map((usage, usageIndex) =>
    usageIndex === index ? { ...usage, ...updates } : usage
  )
}
const buildingPlanCostFactors = computed(
  () =>
    buildingPlanCostFactorsForRoleUsages.value ??
    (props.usageItemsKey === 'landscapeUsageItems'
      ? buildingPlanCostFactorsForLandscapeUsages.value
      : buildingPlanCostFactorsForUsages.value)
)
const buildingPlanServiceFactors = computed(
  () =>
    buildingPlanServiceFactorsForRoleUsages.value ??
    (props.usageItemsKey === 'landscapeUsageItems'
      ? buildingPlanServiceFactorsForLandscapeUsages.value
      : buildingPlanServiceFactorsForUsages.value)
)
const showRepetitionFactor = computed(
  () => props.role === 'CIVIL_ENGINEER' || props.role === 'CIVIL_ENGINEER_EXCAVATION'
)
const showTechnologyFactor = computed(() => !!props.role && technologyFactorRoles.includes(props.role))

function getAccordionKey(index: number): string {
  return `${props.role ?? props.usageItemsKey}:${props.usageItemsKey}:${index}`
}

function onAccordionToggle(index: number, open: boolean): void {
  emit('update:activeAccordionKey', open ? getAccordionKey(index) : null)
}

function showSliders(plan: BuildingPlan | null | undefined): boolean {
  return plan === BuildingPlan.RECONSTRUCTION
}

function normalizeRequirementsLevel(level: unknown): RequirementsLevel {
  return requirementsLevels.includes(level as RequirementsLevel)
    ? (level as RequirementsLevel)
    : DEFAULT_REQUIREMENTS_LEVEL
}

function getRequirementsLevel(usage: SharedUsageItem): RequirementsLevel {
  const usageWithRequirements = usage as UsageWithRequirementsLevel
  return normalizeRequirementsLevel(usageWithRequirements.requirementsLevel)
}

function setRequirementsLevel(index: number, level: RequirementsLevel): void {
  updateUsageItem(index, { requirementsLevel: level })
}

// VAL-154: civil engineers cannot pick reconstruction; the button stays visible but disabled
// with a hint. An already stored reconstruction value is left untouched.
const UNAVAILABLE_BUILDING_PLANS: Partial<Record<Role, readonly BuildingPlan[]>> = {
  CIVIL_ENGINEER: [BuildingPlan.RECONSTRUCTION],
  CIVIL_ENGINEER_EXCAVATION: [BuildingPlan.RECONSTRUCTION],
}

function isBuildingPlanUnavailable(plan: BuildingPlan): boolean {
  return !!props.role && (UNAVAILABLE_BUILDING_PLANS[props.role]?.includes(plan) ?? false)
}

function getBuildingPlanUnavailableTooltip(
  plan: BuildingPlan
): string | { text: string; portal: true; theme: typeof interventionTooltipTheme } {
  if (!isBuildingPlanUnavailable(plan)) return ''
  const text =
    wt('requirementsAndTasks', 'task', 'planType', 'unavailable', plan) ||
    'Für Umbauten oder Sanierungen wird empfohlen, den Aufwand nach effektivem Aufwand abzurechnen.'
  return { text, portal: true, theme: interventionTooltipTheme }
}

function setBuildingPlan(index: number, buildingPlan: BuildingPlan): void {
  if (isBuildingPlanUnavailable(buildingPlan)) return
  updateUsageItem(index, { buildingPlan })
}

function getRequirementsLevelTitle(): string {
  return wt('requirementsAndTasks', 'requirementsLevel', 'title') || 'Requirement level'
}

function getRequirementsLevelLabel(level: RequirementsLevel): string {
  return wt('requirementsAndTasks', 'requirementsLevel', 'option', level) || fallbackRequirementsLevelLabels[level]
}

function getAccordionTitle(usage: SharedUsageItem, index: number): string {
  return getUsageDisplayTitle(usage as UsageWithRequirementsLevel, wt, {
    allUsages: usageItems.value as UsageWithRequirementsLevel[],
    index,
    resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
  })
}

function getBuildingPlanCostFactor(index: number): number {
  return buildingPlanCostFactors.value[index] ?? calculations.getBuildingPlanCostFactor ?? 1
}

function getBuildingPlanServiceFactor(index: number): number {
  return buildingPlanServiceFactors.value[index] ?? calculations.getBuildingPlanServiceFactor ?? 1
}

function getTotalBuildingPlanTaskFactor(index: number): number {
  return getBuildingPlanServiceFactor(index)
}

function getTotalBuildingPlanTaskFactorLabel(): string {
  return wt('requirementsAndTasks', 'task', 'totalBuildingPlanTaskFactor') || 'Gesamtwerkaufgabefaktor'
}

function getRepetitionFactorText(key: 'title' | 'lowLabel' | 'highLabel' | 'valueLabel', fallback: string): string {
  return wt('requirementsAndTasks', 'task', 'repetitionFactor', key) || fallback
}

function getTechnologyFactorText(key: 'title' | 'lowLabel' | 'highLabel' | 'valueLabel', fallback: string): string {
  return wt('requirementsAndTasks', 'task', 'technologyFactor', key) || fallback
}

function formatAdjustmentFactor(value: number): string {
  return value.toFixed(2)
}

function getAccordionAdjustmentsTooltip(index: number): string {
  const title = wt('requirementsAndTasks', 'adjustments', 'title')
  const effortLabel = wt('requirementsAndTasks', 'adjustments', 'option', 'effort')
  const costLabel = wt('requirementsAndTasks', 'adjustments', 'option', 'cost')
  const effortFactor = formatAdjustmentFactor(getBuildingPlanServiceFactor(index))
  const costFactor = formatAdjustmentFactor(getBuildingPlanCostFactor(index))
  return `${title}\n${effortLabel} ×${effortFactor}\n${costLabel} ×${costFactor}`
}

function getAccordionSelectionsSubtitle(usage: SharedUsageItem): string {
  const requirementsLevelTitle = wt('requirementsAndTasks', 'requirementsLevel', 'title') || 'Requirement level'
  const requirementsLevelLabel = getRequirementsLevelLabel(getRequirementsLevel(usage))
  const taskTitle = wt('requirementsAndTasks', 'task', 'title') || 'Task'
  const taskLabel = usage.buildingPlan
    ? wt('requirementsAndTasks', 'task', 'planType', 'option', usage.buildingPlan)
    : '-'

  return `${requirementsLevelTitle}: ${requirementsLevelLabel}&nbsp;&nbsp;·&nbsp;&nbsp;${taskTitle}: ${taskLabel}`
}

function getInterventionTooltip(
  field: InterventionField
): string | { text: string; portal: true; theme: typeof interventionTooltipTheme } {
  const text = wt('requirementsAndTasks', 'task', 'depthOfIntervention', 'tooltip', field)
  if (!text) {
    return ''
  }

  return {
    text,
    portal: true,
    theme: interventionTooltipTheme,
  }
}

function getSliderDraftKey(index: number, field: string): string {
  return `${index}:${field}`
}

function clampRepetitionFactor(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_REPETITION_FACTOR
  const clamped = Math.min(REPETITION_FACTOR_MAX, Math.max(REPETITION_FACTOR_MIN, value))
  return Number((Math.round(clamped / REPETITION_FACTOR_STEP) * REPETITION_FACTOR_STEP).toFixed(1))
}

function getRepetitionFactor(index: number): number {
  const draftValue = sliderDraftValues.value[getSliderDraftKey(index, 'repetitionFactor')]
  if (typeof draftValue === 'number') return draftValue
  return clampRepetitionFactor(usageItems.value[index]?.repetitionFactor ?? DEFAULT_REPETITION_FACTOR)
}

function setRepetitionFactor(index: number, value: number): void {
  updateUsageItem(index, { repetitionFactor: clampRepetitionFactor(value) })
  delete sliderDraftValues.value[getSliderDraftKey(index, 'repetitionFactor')]
}

function onRepetitionFactorInput(index: number, event: Event): void {
  sliderDraftValues.value[getSliderDraftKey(index, 'repetitionFactor')] = clampRepetitionFactor(
    getEventSliderValue(event)
  )
}

function onRepetitionFactorChange(index: number, event: Event): void {
  setRepetitionFactor(index, getEventSliderValue(event))
}

function getRepetitionSliderPercent(value: number): number {
  return ((value - REPETITION_FACTOR_MIN) * 100) / (REPETITION_FACTOR_MAX - REPETITION_FACTOR_MIN)
}

function clampTechnologyFactor(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_TECHNOLOGY_FACTOR
  const clamped = Math.min(TECHNOLOGY_FACTOR_MAX, Math.max(TECHNOLOGY_FACTOR_MIN, value))
  return Number((Math.round(clamped / TECHNOLOGY_FACTOR_STEP) * TECHNOLOGY_FACTOR_STEP).toFixed(1))
}

function getTechnologyFactor(index: number): number {
  const draftValue = sliderDraftValues.value[getSliderDraftKey(index, 'technologyFactor')]
  if (typeof draftValue === 'number') return draftValue
  return clampTechnologyFactor(usageItems.value[index]?.technologyFactor ?? DEFAULT_TECHNOLOGY_FACTOR)
}

function setTechnologyFactor(index: number, value: number): void {
  updateUsageItem(index, { technologyFactor: clampTechnologyFactor(value) })
  delete sliderDraftValues.value[getSliderDraftKey(index, 'technologyFactor')]
}

function onTechnologyFactorInput(index: number, event: Event): void {
  sliderDraftValues.value[getSliderDraftKey(index, 'technologyFactor')] = clampTechnologyFactor(
    getEventSliderValue(event)
  )
}

function onTechnologyFactorChange(index: number, event: Event): void {
  setTechnologyFactor(index, getEventSliderValue(event))
}

function getTechnologySliderPercent(value: number): number {
  return ((value - TECHNOLOGY_FACTOR_MIN) * 100) / (TECHNOLOGY_FACTOR_MAX - TECHNOLOGY_FACTOR_MIN)
}

function getSliderDisplayValue(index: number, field: InterventionField): number {
  const key = getSliderDraftKey(index, field)
  const draftValue = sliderDraftValues.value[key]
  if (typeof draftValue === 'number') return draftValue
  return clampSliderValue(usageItems.value[index]?.[field] ?? DEFAULT_INTERVENTION_SLIDER_VALUE)
}

function formatSliderPercentageDisplay(value: number): string {
  return String(Math.round(value))
}

function getEventSliderValue(event: Event): number {
  return Number((event.target as HTMLInputElement).value)
}

function clampSliderValue(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_INTERVENTION_SLIDER_VALUE
  const clamped = Math.min(INTERVENTION_SLIDER_MAX, Math.max(INTERVENTION_SLIDER_MIN, value))
  return Math.round(clamped / INTERVENTION_SLIDER_STEP) * INTERVENTION_SLIDER_STEP
}

function setSliderDraftValue(index: number, field: InterventionField, value: number): void {
  sliderDraftValues.value[getSliderDraftKey(index, field)] = clampSliderValue(value)
}

function commitSliderValue(index: number, field: InterventionField, value: number): void {
  updateUsageItem(index, { [field]: clampSliderValue(value) })
  delete sliderDraftValues.value[getSliderDraftKey(index, field)]
}

function onSliderInput(index: number, field: InterventionField, event: Event): void {
  setSliderDraftValue(index, field, getEventSliderValue(event))
}

function onSliderChange(index: number, field: InterventionField, event: Event): void {
  commitSliderValue(index, field, getEventSliderValue(event))
}

function onSliderNumberInput(index: number, field: InterventionField, event: Event): void {
  const target = event.target as HTMLInputElement
  if (target.value === '') return

  const parsed = Number(target.value)
  if (!Number.isFinite(parsed)) return

  setSliderDraftValue(index, field, parsed)
}

function onSliderNumberCommit(index: number, field: InterventionField, event: Event): void {
  const target = event.target as HTMLInputElement
  const fallbackValue = getSliderDisplayValue(index, field)
  const parsed = target.value === '' ? fallbackValue : Number(target.value)
  const committedValue = clampSliderValue(parsed)

  target.value = formatSliderPercentageDisplay(committedValue)
  commitSliderValue(index, field, committedValue)
}

function adaptSliderValue(index: number, field: InterventionField, delta: number): void {
  const nextValue = clampSliderValue(getSliderDisplayValue(index, field) + delta)
  commitSliderValue(index, field, nextValue)
}

function calculatePosition(value: number | null | undefined): string {
  const safeValue = clampSliderValue(value ?? DEFAULT_INTERVENTION_SLIDER_VALUE)
  const percent = Number(
    ((safeValue - INTERVENTION_SLIDER_MIN) * 100) / (INTERVENTION_SLIDER_MAX - INTERVENTION_SLIDER_MIN)
  )
  const offsetPx = INTERVENTION_SLIDER_THUMB_RADIUS_PX - (percent / 100) * (INTERVENTION_SLIDER_THUMB_RADIUS_PX * 2)
  return `calc(${percent}% + ${offsetPx}px)`
}

function calculateRepetitionPosition(value: number | null | undefined): string {
  const safeValue = clampRepetitionFactor(value ?? DEFAULT_REPETITION_FACTOR)
  const percent = getRepetitionSliderPercent(safeValue)
  const offsetPx = INTERVENTION_SLIDER_THUMB_RADIUS_PX - (percent / 100) * (INTERVENTION_SLIDER_THUMB_RADIUS_PX * 2)
  return `calc(${percent}% + ${offsetPx}px)`
}

function calculateTechnologyPosition(value: number | null | undefined): string {
  const safeValue = clampTechnologyFactor(value ?? DEFAULT_TECHNOLOGY_FACTOR)
  const percent = getTechnologySliderPercent(safeValue)
  const offsetPx = INTERVENTION_SLIDER_THUMB_RADIUS_PX - (percent / 100) * (INTERVENTION_SLIDER_THUMB_RADIUS_PX * 2)
  return `calc(${percent}% + ${offsetPx}px)`
}
</script>

<style lang="postcss" scoped>
.task-slider-controls {
  @apply flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-5;
}

.task-sliders-panel {
  @apply grid overflow-hidden;
  grid-template-rows: 1fr;
}

.task-sliders-panel-inner {
  @apply min-h-0 overflow-hidden;
}

.task-sliders-enter-active,
.task-sliders-leave-active {
  transition:
    grid-template-rows 180ms ease,
    opacity 140ms ease;
}

.task-sliders-enter-from,
.task-sliders-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

.task-sliders-enter-to,
.task-sliders-leave-from {
  grid-template-rows: 1fr;
  opacity: 1;
}

.task-slider-list {
  @apply mt-2 grid gap-y-2 lg:gap-y-2;
}

.task-slider-scale-title-spacer {
  @apply hidden lg:block;
}

.task-slider-scale-cell {
  @apply min-w-0 -mb-2;
}

.task-slider-control-cell {
  @apply min-w-0;
}

.task-slider-title-cell,
.task-slider-control-cell {
  @apply lg:py-1;
}

.task-slider-title-cell {
  @apply lg:flex lg:items-start;
}

@screen lg {
  .task-slider-list {
    grid-template-columns: max-content minmax(0, 1fr);
    column-gap: 0;
    row-gap: 0.5rem;
    align-items: start;
  }
}

.task-slider-input-group {
  @apply flex w-36 items-center justify-end self-end md:w-40 lg:-mt-2 lg:self-start;
}

.repetition-slider-label {
  @apply min-w-0 max-w-56 truncate;
}

.repetition-slider-end-label {
  @apply block w-auto self-start lg:py-1;
}

.task-slider-scale {
  @apply min-w-0 flex-1;
}

.task-slider-scale-spacer {
  @apply hidden w-36 md:w-40 lg:block;
}

.task-slider-range {
  @apply isolate;
}

.task-slider-range :deep(.core-input-range-wrapper) {
  @apply m-0;
}

.task-slider-markers {
  @apply pointer-events-none absolute z-10 h-0;
  top: 10px;
  left: 10px;
  right: 10px;
}

.task-slider-marker {
  @apply absolute rounded-full bg-neutral-300;
  top: 0;
  width: 12px;
  aspect-ratio: 1;
  margin-left: -6px;
  margin-top: -6px;

  &.active {
    background-color: var(--color-primary-dark);
  }
}

.task-slider-range :deep(.core-input-range) {
  @apply relative z-20 mt-0 h-5;
  margin-bottom: 1.5rem;
  background-size: calc(100% - 20px) 4px;
  background-position: center;
  background-repeat: no-repeat;
}

.task-slider-range :deep(.core-input-range::-webkit-slider-thumb) {
  @apply h-5 w-5 -mt-2 border-2 shadow;
  border-color: var(--color-primary-dark);
}

.task-slider-range :deep(.core-input-range::-moz-range-thumb) {
  @apply h-5 w-5 border-2 shadow;
  border-color: var(--color-primary-dark);
}

.task-slider-range :deep(.core-input-range::-webkit-slider-runnable-track) {
  @apply h-1 rounded-full;
}

.task-slider-range :deep(.core-input-range::-moz-range-track) {
  @apply h-1 rounded-full;
}

.task-factor-row {
  @apply mt-8 flex items-center justify-between border border-neutral-300 bg-neutral-35 p-5;
}

.adjust-value-button {
  @apply inline-flex h-10 w-8 cursor-pointer items-center justify-center text-neutral-550 transition-colors hover:text-black lg:w-10;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

@screen xl {
  .requirements-level-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
